import { PARTNER_PORTAL_PAE_QUEUE } from '../Constants/partnerRoutes';
import { toPartnerIsoDate } from './partnerDateUtils';
import { getPaeQueueDisplayStatus } from './partnerProjectStatusUtils';
import { normalizeLeadershipRegionCode } from './partnerLeadershipUtils';

const REGION_LABELS = {
  LATAM: 'LATAM',
  EMEA: 'EMEA',
  APJ: 'APJ',
  US: 'North America',
};

export const LEADERSHIP_KPI_FILTERS = {
  ALL: 'all',
  REGISTERED_THIS_FY: 'registeredThisFy',
  ACTIVE: 'active',
  ACTIVE_REGISTERED_THIS_FY: 'activeRegisteredThisFy',
  ACTIVE_THIS_FY: 'activeThisFy',
  AT_RISK: 'atRisk',
  COMPLETED: 'completed',
  COMPLETED_REGISTERED_THIS_FY: 'completedRegisteredThisFy',
  COMPLETED_THIS_FY: 'completedThisFy',
};

const LEADERSHIP_KPI_LABELS = {
  [LEADERSHIP_KPI_FILTERS.ALL]: 'All registered projects',
  [LEADERSHIP_KPI_FILTERS.REGISTERED_THIS_FY]: 'Registered this FY',
  [LEADERSHIP_KPI_FILTERS.ACTIVE]: 'Active deliveries',
  [LEADERSHIP_KPI_FILTERS.ACTIVE_REGISTERED_THIS_FY]:
    'Active deliveries · go-live this FY',
  [LEADERSHIP_KPI_FILTERS.ACTIVE_THIS_FY]:
    'Active deliveries · go-live this FY (actual or est. go-live ≥ 04/01/2026)',
  [LEADERSHIP_KPI_FILTERS.AT_RISK]: 'At-risk projects',
  [LEADERSHIP_KPI_FILTERS.COMPLETED]: 'Completed projects',
  [LEADERSHIP_KPI_FILTERS.COMPLETED_REGISTERED_THIS_FY]:
    'Completed projects · go-live this FY',
  [LEADERSHIP_KPI_FILTERS.COMPLETED_THIS_FY]:
    'Completed this FY (Completed or Go-Live Complete · actual or est. go-live ≥ 04/01/2026)',
};

const LEADERSHIP_FY_START_MS = Date.parse('2026-04-01T00:00:00.000Z');

const COMPLETED_THIS_FY_STATUSES = new Set(['Completed', 'Go-Live Complete']);

const parseLeadershipProjectDateMs = (value) => {
  const isoDate = toPartnerIsoDate(value);
  if (isoDate) {
    return Date.parse(`${isoDate}T00:00:00.000Z`);
  }

  const parsed = Date.parse(String(value || '').trim());
  return Number.isNaN(parsed) ? null : parsed;
};

const isOnOrAfterLeadershipFyStart = (value) => {
  const parsedMs = parseLeadershipProjectDateMs(value);
  return parsedMs !== null && parsedMs >= LEADERSHIP_FY_START_MS;
};

export const getProjectGoLiveDateForLeadership = (project) => (
  project?.actualGoliveDate || project?.estimatedGoliveDate
);

export const getLeadershipFyQuarter = (value) => {
  const isoDate = toPartnerIsoDate(value);
  const parsedMs = isoDate
    ? Date.parse(`${isoDate}T00:00:00.000Z`)
    : Date.parse(String(value || '').trim());
  if (Number.isNaN(parsedMs)) {
    return null;
  }
  const month = new Date(parsedMs).getUTCMonth() + 1;
  if (month >= 4 && month <= 6) {
    return 'Q1';
  }
  if (month >= 7 && month <= 9) {
    return 'Q2';
  }
  if (month >= 10 && month <= 12) {
    return 'Q3';
  }
  return 'Q4';
};

export const isProjectGoLiveThisFy = (project) => (
  isOnOrAfterLeadershipFyStart(getProjectGoLiveDateForLeadership(project))
);

export const isActiveThisFyProject = (project) => (
  String(project?.projectStatus || '').trim() === 'Active'
  && isProjectGoLiveThisFy(project)
);

export const isCompletedThisFyProject = (project) => {
  const status = String(project?.projectStatus || '').trim();
  if (!COMPLETED_THIS_FY_STATUSES.has(status)) {
    return false;
  }
  return isProjectGoLiveThisFy(project);
};

export const getLeadershipStatusBucket = (project) => {
  const status = String(project?.projectStatus || '').trim();
  if (!status) {
    return 'Unknown';
  }
  if (status === 'Completed') {
    return 'Completed';
  }
  if (status === 'Active') {
    return 'Active';
  }
  if (status === 'At Risk') {
    return 'At Risk';
  }
  if (status === 'Info Requested') {
    return 'Info Requested';
  }
  if (status === 'Go-Live Complete') {
    return 'Go-Live Complete';
  }
  if (status.startsWith('Sales Cycle')) {
    return 'Sales Cycle';
  }
  return status;
};

export const buildLeadershipPaeQueueUrl = ({
  dimension,
  value,
  status = null,
  kpi = null,
  quarter = null,
}) => {
  const params = new URLSearchParams({ from: 'leadership' });

  if (kpi) {
    params.set('kpi', kpi);
  }

  if (dimension === 'product') {
    params.set('products', value);
  } else if (dimension === 'region') {
    params.set('region', value);
  } else if (dimension === 'engagement') {
    params.set('engagement', value);
  } else if (dimension === 'company') {
    params.set('companyName', value);
    if (status) {
      params.set('leadershipStatus', status);
    }
  }

  if (quarter) {
    params.set('quarter', quarter);
  }

  return `${PARTNER_PORTAL_PAE_QUEUE}?${params.toString()}`;
};

export const buildLeadershipPaeQueueUrlFromKpi = (kpi) => {
  const params = new URLSearchParams({
    from: 'leadership',
    kpi,
  });
  return `${PARTNER_PORTAL_PAE_QUEUE}?${params.toString()}`;
};

export const parseLeadershipPaeQueueParams = (searchParams) => {
  if (searchParams.get('from') !== 'leadership') {
    return null;
  }

  return {
    kpi: searchParams.get('kpi') || '',
    products: searchParams.get('products') || '',
    region: searchParams.get('region') || '',
    companyName: searchParams.get('companyName') || '',
    engagement: searchParams.get('engagement') || '',
    quarter: searchParams.get('quarter') || '',
    leadershipStatus: searchParams.get('leadershipStatus') || '',
  };
};

export const buildColumnFiltersFromLeadershipParams = (params) => {
  if (!params) {
    return {};
  }

  const filters = {};
  if (params.products) {
    filters.products = params.products;
  }
  if (params.region) {
    filters.region = params.region;
  }
  if (params.companyName) {
    filters.companyName = params.companyName;
  }
  if (params.engagement) {
    filters.engagement = params.engagement;
  }

  return filters;
};

export const buildLeadershipFilterLabel = (params) => {
  if (!params) {
    return '';
  }

  const parts = [];
  if (params.kpi && LEADERSHIP_KPI_LABELS[params.kpi]) {
    parts.push(LEADERSHIP_KPI_LABELS[params.kpi]);
  }
  if (params.products) {
    parts.push(`Product: ${params.products}`);
  }
  if (params.region) {
    parts.push(`Region: ${REGION_LABELS[params.region] || params.region}`);
  }
  if (params.companyName) {
    parts.push(`Company: ${params.companyName}`);
  }
  if (params.engagement) {
    parts.push(`Engagement: ${params.engagement}`);
  }
  if (params.quarter) {
    parts.push(`Quarter: ${params.quarter}`);
  }
  if (params.leadershipStatus) {
    parts.push(`Status: ${params.leadershipStatus}`);
  }

  return parts.join(' · ');
};

export const applyLeadershipStatusFilter = (projects, leadershipStatus) => {
  if (!leadershipStatus) {
    return projects;
  }

  return projects.filter(
    (project) => getLeadershipStatusBucket(project) === leadershipStatus,
  );
};

const matchesLeadershipKpiFilter = (project, kpi) => {
  if (!kpi || kpi === LEADERSHIP_KPI_FILTERS.ALL) {
    return true;
  }

  if (kpi === LEADERSHIP_KPI_FILTERS.REGISTERED_THIS_FY) {
    return isOnOrAfterLeadershipFyStart(project.submittedAt);
  }

  if (kpi === LEADERSHIP_KPI_FILTERS.ACTIVE) {
    return String(project?.projectStatus || '').trim() === 'Active';
  }

  if (kpi === LEADERSHIP_KPI_FILTERS.ACTIVE_REGISTERED_THIS_FY
    || kpi === LEADERSHIP_KPI_FILTERS.ACTIVE_THIS_FY) {
    return isActiveThisFyProject(project);
  }

  if (kpi === LEADERSHIP_KPI_FILTERS.COMPLETED) {
    return String(project?.projectStatus || '').trim() === 'Completed';
  }

  if (kpi === LEADERSHIP_KPI_FILTERS.COMPLETED_REGISTERED_THIS_FY) {
    return isCompletedThisFyProject(project);
  }

  if (kpi === LEADERSHIP_KPI_FILTERS.AT_RISK) {
    return String(project?.projectStatus || '').trim() === 'At Risk'
      || getPaeQueueDisplayStatus(project) === 'At Risk';
  }

  if (kpi === LEADERSHIP_KPI_FILTERS.COMPLETED_THIS_FY) {
    return isCompletedThisFyProject(project);
  }

  return true;
};

const matchesLeadershipRegionParam = (project, regionParam) => {
  if (!regionParam) {
    return true;
  }
  return normalizeLeadershipRegionCode(project?.region)
    === normalizeLeadershipRegionCode(regionParam);
};

const matchesLeadershipProductsParam = (project, productParam) => {
  if (!productParam) {
    return true;
  }
  const target = String(productParam).trim().toLowerCase();
  const products = project?.productsInScope || [];
  return products.some(
    (product) => String(product || '').trim().toLowerCase() === target,
  );
};

const matchesLeadershipCompanyParam = (project, companyParam) => {
  if (!companyParam) {
    return true;
  }
  const projectCompany = String(project?.companyName || '').trim().toLowerCase();
  const target = String(companyParam).trim().toLowerCase();
  return projectCompany === target;
};

const matchesLeadershipEngagementParam = (project, engagementParam) => {
  if (!engagementParam) {
    return true;
  }
  const projectEngagement = String(project?.engagementType || '').trim().toLowerCase();
  const target = String(engagementParam).trim().toLowerCase();
  return projectEngagement === target;
};

const matchesLeadershipQuarterParam = (project, quarterParam) => {
  if (!quarterParam) {
    return true;
  }
  const target = String(quarterParam).trim().toUpperCase();
  const quarter = getLeadershipFyQuarter(getProjectGoLiveDateForLeadership(project));
  return quarter === target;
};

export const applyLeadershipQueueFilters = (projects, params) => {
  if (!params) {
    return projects;
  }

  let filtered = projects;

  if (params.kpi) {
    filtered = filtered.filter((project) => matchesLeadershipKpiFilter(project, params.kpi));
  }

  if (params.region) {
    filtered = filtered.filter((project) => matchesLeadershipRegionParam(project, params.region));
  }

  if (params.products) {
    filtered = filtered.filter((project) => matchesLeadershipProductsParam(project, params.products));
  }

  if (params.companyName) {
    filtered = filtered.filter((project) => matchesLeadershipCompanyParam(project, params.companyName));
  }

  if (params.engagement) {
    filtered = filtered.filter((project) => matchesLeadershipEngagementParam(project, params.engagement));
  }

  if (params.quarter) {
    filtered = filtered.filter((project) => matchesLeadershipQuarterParam(project, params.quarter));
  }

  if (params.leadershipStatus) {
    filtered = applyLeadershipStatusFilter(filtered, params.leadershipStatus);
  }

  return filtered;
};

export const getLeadershipKpiInitialStatus = (kpi) => {
  if (kpi === LEADERSHIP_KPI_FILTERS.COMPLETED_THIS_FY
    || kpi === LEADERSHIP_KPI_FILTERS.COMPLETED_REGISTERED_THIS_FY) {
    return 'All';
  }
  if (kpi === LEADERSHIP_KPI_FILTERS.ACTIVE
    || kpi === LEADERSHIP_KPI_FILTERS.ACTIVE_REGISTERED_THIS_FY
    || kpi === LEADERSHIP_KPI_FILTERS.ACTIVE_THIS_FY) {
    return 'All';
  }
  if (kpi === LEADERSHIP_KPI_FILTERS.AT_RISK) {
    return 'At Risk';
  }
  return 'All';
};
